import {EntityStoreUser} from 'constants/EntityStoreConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {EntitySummary, ProclaimedStatus, User, UserSummary} from 'constants/UserConstants';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {PartySummary} from 'parties/PartyConstants';

import {SourceSummaryGraphQL} from '../../../../graphql-fragments/nav/sources/__generated/SourceSummaryGraphQL';
import {AuthorGraphQL} from '../../../../graphql-fragments/parties/__generated/AuthorGraphQL';
import {CreatorGraphQL} from '../../../../graphql-fragments/parties/__generated/CreatorGraphQL';
import {PartySummaryGraphQL} from '../../../../graphql-fragments/parties/__generated/PartySummaryGraphQL';
import {PrincipalGraphQL} from '../../../../graphql-fragments/parties/__generated/PrincipalGraphQL';
import {UserGraphQL} from '../../../../graphql-fragments/parties/__generated/UserGraphQL';

export function makeUser(name?: string | undefined): User {
  return name ? {...USER, name} : {...USER};
}

// This corresponds to `User.to_dict()`
export const USER: User = {
  academic: false,
  avatar_url: '/static/img/no_avatar.jpg',
  email: 'otheruser@benchling.com',
  handle: 'otherUser',
  id: 'ent_zEKfIFZs',
  institution: null,
  isTenantAdmin: false,
  joinedOn: '2016-09-20T22:00:29.743158+00:00',
  name: 'Richard Feynman',
  proclaimed: null,
  status: null,
  suspended: false,
  type: ModelTypeConstants.USER,
  url_identifier: 'otheruser',
};

export const USER_GQL: UserGraphQL = {
  __typename: 'UserNode',
  id: USER.id,
  name: USER.name,
  handle: USER.handle,
  email: USER.email,
  avatarUrl: USER.avatar_url,
  joinedOn: USER.joinedOn,
  urlIdentifier: USER.url_identifier,
  isTenantAdmin: USER.isTenantAdmin,
  academic: USER.academic,
  suspended: USER.suspended,
};

export const USER_SUMMARY: UserSummary = {
  id: USER.id,
  handle: USER.handle,
  avatar_url: USER.avatar_url,
};

export const USER_2: User = {
  academic: false,
  avatar_url: '/static/img/no_avatar.jpg',
  email: 'a.einstein@benchling.com',
  handle: 'AEinstein',
  id: 'ent_Amnvo2nv',
  institution: null,
  isTenantAdmin: false,
  joinedOn: '2016-09-20T22:00:29.743158+00:00',
  name: 'Albert Einstein',
  proclaimed: ProclaimedStatus.COMMERCIAL,
  status: null,
  suspended: false,
  type: ModelTypeConstants.USER,
  url_identifier: 'aeinstein',
};

export const USER_2_GQL: UserGraphQL = {
  __typename: 'UserNode',
  id: USER_2.id,
  name: USER_2.name,
  handle: USER_2.handle,
  email: USER_2.email,
  avatarUrl: USER_2.avatar_url,
  joinedOn: USER_2.joinedOn,
  urlIdentifier: USER_2.url_identifier,
  isTenantAdmin: USER_2.isTenantAdmin,
  academic: USER_2.academic,
  suspended: USER_2.suspended,
};

export const CURRENT_USER: User = {
  academic: false,
  avatar_url: '/static/img/no_avatar.jpg',
  email: 'L.Pasteur@benchling.com',
  handle: 'L.Pasteur',
  isTenantAdmin: false,
  id: 'ent_gDSXQchy',
  institution: null,
  joinedOn: '2017-10-30T05:31:21.505780+00:00',
  name: 'Louis Pasteur',
  proclaimed: ProclaimedStatus.COMMERCIAL,
  status: null,
  suspended: false,
  type: ModelTypeConstants.USER,
  url_identifier: 'l-pasteur',
};

export const CURRENT_USER_GQL: UserGraphQL = {
  __typename: 'UserNode',
  id: CURRENT_USER.id,
  name: CURRENT_USER.name,
  handle: CURRENT_USER.handle,
  email: CURRENT_USER.email,
  avatarUrl: CURRENT_USER.avatar_url,
  joinedOn: CURRENT_USER.joinedOn,
  urlIdentifier: CURRENT_USER.url_identifier,
  isTenantAdmin: CURRENT_USER.isTenantAdmin,
  academic: CURRENT_USER.academic,
  suspended: CURRENT_USER.suspended,
};

export const BENCHLING_ADMIN_USER: User = {
  academic: false,
  admin: true,
  avatar_url: '/static/img/no_avatar.jpg',
  email: 'otheruser.admin@benchling.com',
  handle: 'otherUser.admin',
  id: 'ent_zEKfIFZs',
  institution: null,
  isTenantAdmin: true,
  joinedOn: '2016-09-20T22:00:29.743158+00:00',
  name: 'Richard Feynman',
  proclaimed: ProclaimedStatus.COMMERCIAL,
  status: null,
  suspended: false,
  type: ModelTypeConstants.USER,
  url_identifier: 'otheruser_admin',
};

// This corresponds to the `currentUser` object that is loaded in EntityStore
export const ENTITY_STORE_USER: EntityStoreUser = {
  ...USER,
  proclaimed: ProclaimedStatus.COMMERCIAL,
  organizationIds: [],
  organizations: [],
  teamIds: [],
  teams: [],
  currentUserInboundRequests: [],
  pendingOrgInvites: 0,
  userOrgsInboundRequests: [],
};

// This corresponds to `User.to_dict(public=True)`
export const PUBLIC_USER = {
  __typename: 'UserNode',
  avatar_url: '/static/img/no_avatar.jpg',
  handle: 'otherUser',
  id: 'ent_zEKfIFZs',
  institution: null,
  joinedOn: '2016-09-20T22:00:29.743158+00:00',
  name: 'Richard Feynman',
  type: ModelTypeConstants.USER,
  url_identifier: 'otheruser',
};

export const SOURCE_USER_SUMMARY: EntitySummary = {
  avatar_url: '/static/img/no_avatar.jpg',
  handle: 'lpasteur',
  id: 'ent_GLriNV3D',
  joinedOn: '2020-03-19T18:00:11.273783+00:00',
  name: 'Louis Pasteur',
  type: ModelTypeConstants.USER,
  url_identifier: 'l-pasteur',
};

export const SOURCE_USER_SUMMARY_GQL: ExtractByTypename<SourceSummaryGraphQL['creator'], 'UserNode'> = {
  __typename: 'UserNode',
  avatarUrl: SOURCE_USER_SUMMARY.avatar_url,
  handle: SOURCE_USER_SUMMARY.handle,
  id: SOURCE_USER_SUMMARY.id,
  joinedOn: SOURCE_USER_SUMMARY.joinedOn,
  name: SOURCE_USER_SUMMARY.name,
  urlIdentifier: SOURCE_USER_SUMMARY.url_identifier,
};

// The following are used more for declarative translations testing purposes, usually one of the above will be
// preferred as test fixtures.

export const PARTY_SUMMARY_USER: PartySummary = {
  id: 'ent_p307fj2',
  name: 'Party Feynman',
  handle: 'party',
  avatar_url: '/static/img/no_avatar.jpg',
  type: ModelTypeConstants.USER,
};

export const PARTY_SUMMARY_USER_GQL: PartySummaryGraphQL = {
  __typename: 'UserNode',
  id: PARTY_SUMMARY_USER.id,
  name: PARTY_SUMMARY_USER.name,
  handle: PARTY_SUMMARY_USER.handle,
  avatarUrl: PARTY_SUMMARY_USER.avatar_url!,
};

export const PRINCIPAL_USER: User = {
  ...PARTY_SUMMARY_USER,
  id: 'ent_p402fj2',
  email: 'principal@benchling.com',
  handle: 'principal',
  avatar_url: USER.avatar_url,
  type: ModelTypeConstants.USER,
  academic: USER.academic,
  institution: USER.institution,
  isTenantAdmin: USER.isTenantAdmin,
  joinedOn: USER.joinedOn,
  proclaimed: null,
  status: USER.status,
  suspended: USER.suspended,
  url_identifier: USER.url_identifier,
};

export const PRINCIPAL_USER_GQL: PrincipalGraphQL = {
  ...PARTY_SUMMARY_USER_GQL,
  __typename: 'UserNode',
  id: PRINCIPAL_USER.id,
  email: PRINCIPAL_USER.email,
  handle: PRINCIPAL_USER.handle,
  joinedOn: PRINCIPAL_USER.joinedOn,
  urlIdentifier: PRINCIPAL_USER.url_identifier,
  isTenantAdmin: PRINCIPAL_USER.isTenantAdmin,
  academic: PRINCIPAL_USER.academic,
  suspended: PRINCIPAL_USER.suspended,
};

export const AUTHOR: User = {
  ...PRINCIPAL_USER,
  id: 'ent_a901fj2',
  email: 'author@benchling.com',
  handle: 'author',
};

export const AUTHOR_GQL: AuthorGraphQL = {
  ...PRINCIPAL_USER_GQL,
  __typename: 'UserNode',
  id: AUTHOR.id,
  email: AUTHOR.email,
  handle: AUTHOR.handle,
};

export const CREATOR: User = {
  ...PRINCIPAL_USER,
  id: 'ent_cx01fj2',
};

export const CREATOR_GQL: CreatorGraphQL = {
  ...PRINCIPAL_USER_GQL,
  __typename: 'UserNode',
  id: CREATOR.id,
};
